import React, { ChangeEvent } from "react";
import { Localized } from "@fluent/react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SendIcon from "@mui/icons-material/Send";
import UploadIcon from "@mui/icons-material/Upload";
import EditIcon from "@mui/icons-material/Edit";
import { Alert, Box, Button, Card, CardContent, CardHeader, Divider, Grid, LinearProgress, Table, TableBody, TableCell, TableCellProps, TableContainer, TableHead, TableRow } from "@mui/material";
import { Container } from "@mui/system";

export enum SubmitError {
	NetworkError = "network",
	PermissionError = "permission",
	ConflictError = "conflict",
	InvalidError = "invalid",
	Bug = "bug",
}

export enum ImportError {
	InvalidSheet = "invalid",
	EmptySheet = "empty",
}

export type Row = {
	id: number;
	fields: Fields;
	error: SubmitError | null;
};

export type Fields = {
	givenName: string,
	familyName: string,
	email: string,
}

export type InviteUsersUIProps = {
	pending: boolean,
	rows: Row[],
	importError: ImportError | null,
	onAddRowClick(): void,
	onRemoveRowClick(rowID: number): void,
	onFieldChange(rowID: number, field: keyof Fields, value: string): void,
	onImport(event: ChangeEvent<HTMLInputElement>): void,
	onSubmit?: () => void,
}

export default function InviteUsersUI(props: InviteUsersUIProps) {
	return (
		<Container
			component="form"
			onSubmit={e => {
				e.preventDefault();
				props.onSubmit && props.onSubmit();
			}}
			maxWidth="lg"
			sx={{ mt: 4, mb: 4, flexGrow: 1 }}
		>
			{/* The purpose of this button is to prevent implicit submission
			when the user presses 'return'. For a form as big as this one, it
			is unlikely that the user wanted to submit it by pressing 'return'
			on a random input. Details: https://stackoverflow.com/a/51507806 */}
			<button type="submit" disabled style={{ display: "none" }} aria-hidden="true" />

			{props.pending
				? <LinearProgress sx={{ height: "8px" }} />
				: <Box sx={{ height: "8px" }} />
			}

			<Card>
				<Localized id="invite-users-header" attrs={{ title: true }}>
					<CardHeader />
				</Localized>
				{props.rows.length > 0
					? <InviteUsersTable
						disabled={props.pending}
						allowSubmit={props.onSubmit != null}
						rows={props.rows}
						onAddRowClick={props.onAddRowClick}
						onRemoveRowClick={props.onRemoveRowClick}
						onFieldChange={props.onFieldChange}
					/>
					: <GettingStarted
						importError={props.importError}
						onAddRowClick={props.onAddRowClick}
						onImport={props.onImport}
					/>
				}
			</Card>
		</Container>
	);
}

type GettingStartedProps = {
	importError: ImportError | null,
	onAddRowClick(): void,
	onImport(event: ChangeEvent<HTMLInputElement>): void,
}

function GettingStarted(props: GettingStartedProps) {
	return <CardContent>
		<Grid container>
			<Grid item xs>
				<ol>
					<li>
						<p><strong><Localized id="invite-users-getting-started-download-title" /></strong></p>
						<p><Localized id="invite-users-getting-started-download-description" /></p>
						<p>
							<Localized id="invite-users-getting-started-download-button" attrs={{ href: true }}>
								<Button
									component="a"
									download
									startIcon={<DownloadIcon />}
									variant="contained" />
							</Localized>
						</p>
					</li>
					<li>
						<p><strong><Localized id="invite-users-getting-started-import-title" /></strong></p>
						<p><Localized id="invite-users-getting-started-import-description" /></p>
						<p>
							<Button
								component="label"
								startIcon={<UploadIcon />}
								variant="contained">
								<Localized id="invite-users-getting-started-import-button" />
								<input
									type="file"
									onChange={props.onImport}
									hidden />
							</Button>
						</p>
						{props.importError && <p>
							<Localized id={`invite-users-import-error-${props.importError}`}>
								<Alert severity="error" />
							</Localized>
						</p>}
					</li>
				</ol>
			</Grid>
			<Localized id="invite-users-getting-started-divider">
				<Divider orientation="vertical" flexItem />
			</Localized>
			<Grid item xs sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center'
			}}>
				<Localized id="invite-users-getting-started-manual-entry-button">
					<Button
						onClick={props.onAddRowClick}
						variant="outlined"
						color="success"
						startIcon={<EditIcon />}
					/>
				</Localized>
			</Grid>
		</Grid>
	</CardContent>
}

type InviteUsersTableProps = {
	disabled?: boolean,
	allowSubmit: boolean,
	rows: Row[],
	onAddRowClick(): void,
	onRemoveRowClick(rowID: number): void,
	onFieldChange(rowID: number, field: keyof Fields, value: string): void,
}

function InviteUsersTable(props: InviteUsersTableProps) {
	return <TableContainer
		sx={{ p: 2, display: "flex", flexDirection: "column" }}
	>
		<Box>
			<Localized id="invite-users-send-invites">
				<Button
					type="submit"
					variant="contained"
					disabled={props.disabled || !props.allowSubmit}
					startIcon={<SendIcon />}
				/>
			</Localized>
		</Box>
		<Box sx={{
			minHeight: "0", width: '100%', margin: "1rem 0"
		}}>
			{props.rows.length > 0 &&
				<Table size="small" padding="none">
					<TableHead>
						<TableRow>
							<Localized id="invite-users-columns-email">
								<TableCell />
							</Localized>
							<Localized id="invite-users-columns-given-name">
								<TableCell />
							</Localized>
							<Localized id="invite-users-columns-family-name">
								<TableCell />
							</Localized>
							{/* Empty tablecell to serve as the heading
							for the remove button */}
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{props.rows.map(row =>
							<RowInputMemo
								key={row.id}
								fields={row.fields}
								error={row.error}
								disabled={props.disabled}
								onFieldChange={(field, value) => props.onFieldChange(row.id, field, value)}
								onRemoveClick={() => props.onRemoveRowClick(row.id)}
							/>
						)}
					</TableBody>
				</Table>
			}
		</Box>
		<Box
			width="100%"
			display="flex"
			justifyContent="space-between"
		>
			<Localized id="invite-users-add-row">
				<Button
					onClick={props.onAddRowClick}
					sx={{
						color: "green",
						borderColor: "green",
						marginLeft: "auto",
						marginRight: "0"
					}}
					variant="outlined"
					startIcon={<AddCircleOutlineIcon />}
				/>
			</Localized>
		</Box>
			
	</TableContainer>;
}

type RowInputProps = {
	fields: Fields,
	error: SubmitError | null
	disabled?: boolean,
	onFieldChange(field: keyof Fields, value: string): void,
	onRemoveClick(): void,
};

function RowInput(props: RowInputProps) {
	let sx = {
		borderBottom: props.error ? '0' : undefined,
	}
	
	return <>
		<TableRow hover>
			<Field
				type="email"
				// The email is required if any of the other fields are filled in.
				// This means that we won't complain about empty rows when
				// submitting, but we will complain if the email is not there but
				// other fields are there.
				required={props.fields.givenName !== "" || props.fields.familyName !== ""}
				value={props.fields.email}
				disabled={props.disabled}
				sx={sx}
				onChange={value => props.onFieldChange("email", value)}
			/>
			<Field
				type="text"
				value={props.fields.givenName}
				disabled={props.disabled}
				sx={sx}
				onChange={value => props.onFieldChange("givenName", value)}
			/>
			<Field
				type="text"
				value={props.fields.familyName}
				disabled={props.disabled}
				sx={sx}
				onChange={value => props.onFieldChange("familyName", value)}
			/>
			<TableCell sx={sx}>
				<Button
					color="error"
					disabled={props.disabled}
					onClick={props.onRemoveClick}
				>
					<RemoveCircleOutlineIcon />
				</Button>
			</TableCell>
		</TableRow>
		{props.error != null &&
			<TableRow>
				<TableCell colSpan={4}>
					<Feedback error={props.error} />
				</TableCell>
				<TableCell />
			</TableRow>
		}
	</>
}

const RowInputMemo = React.memo(RowInput,
	(prevProps, nextProps) =>
		prevProps.fields === nextProps.fields &&
		prevProps.error === nextProps.error &&
		prevProps.disabled === nextProps.disabled);

type FieldProps = {
	type: React.HTMLInputTypeAttribute,
	value: string,
	required?: boolean,
	disabled?: boolean,
	sx: TableCellProps["sx"],
	onChange(value: string): void,
}

function Field(props: FieldProps) {
	return <TableCell sx={props.sx}>
		<Box
			component="input"
			type={props.type}
			value={props.value}
			required={props.required}
			disabled={props.disabled}
			onChange={e => props.onChange(e.target.value)}
			sx={{
				color: "black",
				backgroundColor: "white",
				borderRadius: "5px",
				padding: "5px",
				margin: "1rem 0",
				border: "lightgray 2px solid"
			}}
		/>
	</TableCell>;
}

type FeedbackProps = {
	error: SubmitError,
}

function Feedback(props: FeedbackProps) {
	return <Localized id={`invite-users-error-${props.error}`}>
		<Alert sx={{ margin: '8px' }} severity="error" />
	</Localized>
}
